import { useEffect, useState, useCallback } from "react";
import { Sparkle, ArrowsClockwise, EnvelopeSimple, Lock } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

/**
 * WeeklyReflectionCard — Hush Plus exclusive.
 *
 *  - Free users see a soft paywall pitch ("Upgrade to Hush Plus to unlock").
 *  - Plus users see their latest deep reflection letter. If none exists yet,
 *    they see a "Generate this week's reflection" button (requires ≥3 entries
 *    this ISO week — server enforces).
 *  - The Sunday cron auto-generates + emails this for every Plus user, so most
 *    of the time this card simply renders the latest letter.
 */
export default function WeeklyReflectionCard({ user, onUpgradeClick }) {
  const isPlus = Boolean(user?.subscription_active);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!isPlus) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/journal/weekly-reflection");
      setDoc(data);
      setErr("");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setDoc(null);
        setErr("");
      } else if (status !== 402) {
        setErr(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [isPlus]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/journal/weekly-reflection/generate");
      setDoc(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Free tier — soft paywall
  if (!isPlus) {
    return (
      <div
        className="card p-6 grain border-brand/30 bg-brand/[0.04]"
        data-testid="weekly-reflection-paywall"
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={20} weight="fill" className="text-brand" />
          <p className="eyebrow text-brand">Hush Plus · Sunday letter</p>
        </div>
        <h3 className="font-serif text-2xl text-ink-900 mb-2">
          A deeper reflection, written back to you every Sunday.
        </h3>
        <p className="text-[15px] text-ink-700 leading-relaxed mb-4">
          Plus members get a long-form, letter-style reflection on the week's emotional arc — delivered
          in-app and to your inbox every Sunday morning.
        </p>
        <button
          type="button"
          onClick={onUpgradeClick}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="weekly-reflection-upgrade-btn"
        >
          <Lock size={14} /> Unlock with Hush Plus
        </button>
      </div>
    );
  }

  // Plus — loading
  if (loading) {
    return (
      <div className="card p-6" data-testid="weekly-reflection-loading">
        <p className="eyebrow text-brand mb-2">Plus · Sunday letter</p>
        <p className="text-sm text-ink-500">Loading your weekly reflection…</p>
      </div>
    );
  }

  // Plus — no reflection yet this week
  if (!doc) {
    return (
      <div className="card p-6 grain" data-testid="weekly-reflection-empty">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={20} weight="fill" className="text-brand" />
          <p className="eyebrow text-brand">Plus · Sunday letter</p>
        </div>
        <h3 className="font-serif text-xl text-ink-900 mb-2">
          Your first deep reflection is on its way.
        </h3>
        <p className="text-[14px] text-ink-700 leading-relaxed mb-4">
          We'll write you a private letter every Sunday morning once you've logged at least three entries
          this week. Want to read this week's now?
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={generate}
            className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="weekly-reflection-generate-btn"
          >
            {busy ? "Writing…" : "Write this week's reflection"}
          </button>
        </div>
        {err && (
          <p className="mt-3 text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2">
            {err}
          </p>
        )}
      </div>
    );
  }

  // Plus — show the letter
  const weekLabel = (() => {
    try {
      const s = new Date(doc.week_start);
      const e = new Date(doc.week_end);
      const sf = s.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      const ef = e.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      return `Week of ${sf} — ${ef}`;
    } catch (_) {
      return doc.week_key;
    }
  })();

  return (
    <div className="card p-6 sm:p-8 grain" data-testid="weekly-reflection-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="fill" className="text-brand" />
          <p className="eyebrow text-brand">Plus · Sunday letter</p>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          className="text-xs text-ink-500 hover:text-brand inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="weekly-reflection-refresh-btn"
          aria-label="Refresh"
          title="Re-fetch this week's letter"
        >
          <ArrowsClockwise size={12} /> {busy ? "…" : "Refresh"}
        </button>
      </div>
      <h3 className="font-serif text-2xl sm:text-3xl text-ink-900 leading-tight mb-1">
        Your week, {user?.name?.split(" ")[0] || "friend"}.
      </h3>
      <p className="text-xs text-ink-500 mb-5 inline-flex items-center gap-1.5">
        <EnvelopeSimple size={12} /> {weekLabel} · sent to {user?.email}
      </p>
      <div
        className="font-serif text-[16px] sm:text-[17px] leading-[1.78] text-ink-900 space-y-4"
        data-testid="weekly-reflection-letter"
      >
        {String(doc.letter || "").split(/\n+/).filter(Boolean).map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
      {err && (
        <p className="mt-4 text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2">
          {err}
        </p>
      )}
    </div>
  );
}
